import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Box,
    UserCircle,
    Building2,
    Calendar,
    Save,
    Signature,
    Check
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import api from '../../utils/api';
import { Asset } from '../../types/asset';
import { User, Department } from '../../types';
import { toast } from 'sonner';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from '../../components/AccessRestricted';

const ReassignAsset: React.FC = () => {
    const ability = useAbility(AbilityContext);
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [asset, setAsset] = useState<Asset | null>(null);
    const [users, setUsers] = useState<User[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Form State
    const [assignTargetType, setAssignTargetType] = useState<'user' | 'department'>('user');
    const [selectedUser, setSelectedUser] = useState<string>('');
    const [selectedDepartment, setSelectedDepartment] = useState<string>('');
    const [reassignedDate, setReassignedDate] = useState(new Date().toISOString().split('T')[0]);
    const [notes, setNotes] = useState('');

    // Signature 1: Manager (Mandatory)
    const [managerSignatureType, setManagerSignatureType] = useState<'Draw' | 'Type'>('Type');
    const [managerSignatureData, setManagerSignatureData] = useState('');
    const [managerTypedSignature, setManagerTypedSignature] = useState('');
    const [isManagerDrawing, setIsManagerDrawing] = useState(false);
    const managerCanvasRef = useRef<HTMLCanvasElement>(null);

    // Signature 2: Current Assignee (Optional)
    const [currentAssigneeSignatureType, setCurrentAssigneeSignatureType] = useState<'Draw' | 'Type'>('Type');
    const [currentAssigneeSignatureData, setCurrentAssigneeSignatureData] = useState('');
    const [currentAssigneeTypedSignature, setCurrentAssigneeTypedSignature] = useState('');
    const [isCurrentAssigneeDrawing, setIsCurrentAssigneeDrawing] = useState(false);
    const currentAssigneeCanvasRef = useRef<HTMLCanvasElement>(null);

    // Signature 3: New Assignee (Optional)
    const [newAssigneeSignatureType, setNewAssigneeSignatureType] = useState<'Draw' | 'Type'>('Type');
    const [newAssigneeSignatureData, setNewAssigneeSignatureData] = useState('');
    const [newAssigneeTypedSignature, setNewAssigneeTypedSignature] = useState('');
    const [isNewAssigneeDrawing, setIsNewAssigneeDrawing] = useState(false);
    const newAssigneeCanvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [assetRes, usersRes, deptsRes] = await Promise.all([
                    api.get<Asset>(`/api/assets/${id}`),
                    api.get<{ users: User[] }>('/api/users?all_users=true'),
                    api.get<{ departments: Department[] }>('/api/departments')
                ]);
                setAsset(assetRes);
                setUsers(usersRes.users || []);
                setDepartments(deptsRes.departments || []);
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load required data');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    if (!ability.can(ACTIONS.UPDATE, RESOURCES.ASSET_ASSIGNMENT)) {
        return <AccessRestricted />;
    }

    // Generic Signature Helpers
    const drawSignature = (
        e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>,
        canvas: HTMLCanvasElement | null,
        isStart: boolean
    ) => {
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const rect = canvas.getBoundingClientRect();

        let clientX = 0;
        let clientY = 0;
        if ('touches' in e) {
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
        } else {
            clientX = e.clientX;
            clientY = e.clientY;
        }

        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        const x = (clientX - rect.left) * scaleX;
        const y = (clientY - rect.top) * scaleY;

        if (isStart) {
            ctx.beginPath();
            ctx.moveTo(x, y);
        } else {
            ctx.lineTo(x, y);
            ctx.stroke();
        }
    };

    const clearCanvas = (canvas: HTMLCanvasElement | null) => {
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    const getSignatureData = (type: string, typedVal: string, canvasRef: React.RefObject<HTMLCanvasElement>, existingData: string) => {
        if (type === 'Type') {
            return typedVal.trim();
        } else if (canvasRef.current) {
            return canvasRef.current.toDataURL();
        }
        return existingData;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!asset) return;

        if (assignTargetType === 'user' && !selectedUser) {
            toast.error('Please select a new assignee');
            return;
        }

        if (assignTargetType === 'department' && !selectedDepartment) {
            toast.error('Please select a new department assignee');
            return;
        }

        // Process Manager Signature (Mandatory)
        const finalManagerSignature = getSignatureData(managerSignatureType, managerTypedSignature, managerCanvasRef, managerSignatureData);
        if (!finalManagerSignature) {
            toast.error('Manager signature is mandatory');
            return;
        }

        // Process Assignee Signatures (Optional)
        const finalCurrentAssigneeSignature = getSignatureData(currentAssigneeSignatureType, currentAssigneeTypedSignature, currentAssigneeCanvasRef, currentAssigneeSignatureData);
        const finalNewAssigneeSignature = getSignatureData(newAssigneeSignatureType, newAssigneeTypedSignature, newAssigneeCanvasRef, newAssigneeSignatureData);

        try {
            setSubmitting(true);

            const payload = {
                assignedToUserId: assignTargetType === 'user' ? parseInt(selectedUser) : null,
                assignedToDepartmentId: assignTargetType === 'department' ? parseInt(selectedDepartment) : null,
                reassignedDate,
                notes,
                managerSignatureType,
                managerSignatureData: finalManagerSignature,
                returnSignatureType: currentAssigneeSignatureType,
                returnSignatureData: finalCurrentAssigneeSignature,
                newAssigneeSignatureType,
                newAssigneeSignatureData: finalNewAssigneeSignature
            };

            await api.post(`/api/assets/${asset.id}/reassign`, payload);

            toast.success('Asset successfully reassigned');
            navigate(`/assets/${asset.id}`);
        } catch (error: any) {
            console.error('Error reassigning asset:', error);
            toast.error(error.response?.data?.message || 'Failed to reassign asset');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!asset || (!asset.assignedToUser && !asset.assignedToDepartment)) {
        return (
            <div className="text-center py-20">
                <h2 className="text-2xl font-bold text-gray-900">Asset cannot be reassigned</h2>
                <p className="text-gray-500 mt-2">Asset must be currently assigned to someone to be reassigned.</p>
                <Button onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
            </div>
        );
    }

    return (
        <div className="max-w-[1000px] mx-auto pb-20 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex items-center gap-4 mb-4">
                <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-xl border border-gray-100 bg-white hover:bg-gray-50"
                    onClick={() => navigate(-1)}
                >
                    <ArrowLeft size={20} />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                        Reassign Asset
                    </h1>
                    <p className="text-gray-500 text-sm font-medium mt-0.5">
                        Transfer custody directly to a new assignee
                    </p>
                </div>
            </div>

            {/* Banner */}
            <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-4 mb-8 flex items-start gap-4 shadow-sm">
                <div className="p-2 bg-blue-100 rounded-xl shrink-0 mt-0.5">
                    <Check size={18} className="text-blue-600" />
                </div>
                <div>
                    <h3 className="font-bold text-blue-900 text-sm mb-1">Direct Transfer Workflow</h3>
                    <p className="text-blue-800 text-sm font-medium leading-relaxed">
                        This captures the return from the current assignee and the delivery to the new assignee in one easy step.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Form */}
                <div className="lg:col-span-2 space-y-8">
                    <form onSubmit={handleSubmit} className="space-y-8">

                        {/* 1. Asset & Current Assignee Details */}
                        <section className="bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                            <div className="flex items-center gap-3">
                                <Box size={24} className="text-blue-500" />
                                <div>
                                    <h2 className="font-bold text-gray-900 text-lg">Asset & Custody Details</h2>
                                    <p className="text-gray-500 text-xs font-medium">Current information</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-6 bg-gray-50 p-6 rounded-2xl border border-gray-100">
                                <div>
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Asset Name</span>
                                    <p className="font-bold text-gray-900">{asset.name} ({asset.tag})</p>
                                </div>
                                <div>
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Current Assignee</span>
                                    <div className="flex items-center gap-2">
                                        {asset.assignedToUser ? <UserCircle size={16} className="text-blue-500" /> : <Building2 size={16} className="text-blue-500" />}
                                        <p className="font-bold text-gray-900">
                                            {asset.assignedToUser?.name || asset.assignedToDepartment?.departmentName}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* 2. New Assignment Details */}
                        <section className="bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                            <div className="flex items-center gap-3">
                                <UserCircle size={24} className="text-indigo-500" />
                                <div>
                                    <h2 className="font-bold text-gray-900 text-lg">New Assignee</h2>
                                    <p className="text-gray-500 text-xs font-medium">Who is receiving this asset?</p>
                                </div>
                            </div>

                            <div className="space-y-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1 block">Assign To <span className="text-red-500">*</span></label>
                                    <div className="grid grid-cols-2 gap-2 p-1 bg-gray-50 rounded-xl border border-gray-100">
                                        <button
                                            type="button"
                                            onClick={() => setAssignTargetType('user')}
                                            className={`py-2 text-sm font-bold rounded-lg transition-all ${assignTargetType === 'user' ? 'bg-white text-blue-600 shadow-sm border border-gray-200/50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100/50'}`}
                                        >
                                            Person
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setAssignTargetType('department')}
                                            className={`py-2 text-sm font-bold rounded-lg transition-all ${assignTargetType === 'department' ? 'bg-white text-blue-600 shadow-sm border border-gray-200/50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100/50'}`}
                                        >
                                            Department
                                        </button>
                                    </div>
                                </div>

                                {assignTargetType === 'user' && (
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Select Person <span className="text-red-500">*</span></label>
                                        <Select value={selectedUser} onValueChange={setSelectedUser}>
                                            <SelectTrigger className="w-full rounded-xl h-12 px-4 border-gray-200">
                                                <SelectValue placeholder="Select user..." />
                                            </SelectTrigger>
                                            <SelectContent className="rounded-xl">
                                                {users.filter(u => u.id.toString() !== asset.assignedToUser?.id?.toString()).map(user => (
                                                    <SelectItem key={user.id} value={user.id.toString()}>
                                                        {user.name} ({user.jobRole || 'User'})
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                {assignTargetType === 'department' && (
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Select Department <span className="text-red-500">*</span></label>
                                        <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                                            <SelectTrigger className="w-full rounded-xl h-12 px-4 border-gray-200">
                                                <SelectValue placeholder="Select department..." />
                                            </SelectTrigger>
                                            <SelectContent className="rounded-xl">
                                                {departments.filter(d => d.id.toString() !== asset.assignedToDepartment?.id?.toString()).map(dept => (
                                                    <SelectItem key={dept.id} value={dept.id.toString()}>
                                                        {dept.departmentName}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Transfer Date <span className="text-red-500">*</span></label>
                                        <Input
                                            type="date"
                                            value={reassignedDate}
                                            onChange={(e) => setReassignedDate(e.target.value)}
                                            className="rounded-xl h-12 px-4 border-gray-200"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Reassignment Notes</label>
                                    <Textarea
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        className="rounded-xl min-h-[80px] border-gray-200 p-4"
                                        placeholder="Add any specific context about the transfer..."
                                    />
                                </div>
                            </div>
                        </section>

                        {/* 3. Signatures */}
                        <section className="bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-8">
                            <div className="flex items-center gap-3 mb-6">
                                <Signature size={24} className="text-gray-900" />
                                <div>
                                    <h2 className="font-bold text-gray-900 text-lg">Signatures</h2>
                                    <p className="text-gray-500 text-xs font-medium">Verify handover custody</p>
                                </div>
                            </div>

                            {/* Manager Signature (Mandatory) */}
                            <div className="space-y-4 p-6 bg-blue-50/30 border border-blue-100 rounded-2xl">
                                <div className="flex items-center justify-between">
                                    <label className="text-xs font-bold text-gray-900 uppercase tracking-wide">Manager / Approver Signature <span className="text-red-500">*</span></label>
                                    <div className="flex gap-2">
                                        <Button type="button" size="sm" variant={managerSignatureType === 'Type' ? 'default' : 'outline'} onClick={() => setManagerSignatureType('Type')} className="h-8 rounded-lg text-xs font-bold px-4">Type</Button>
                                        <Button type="button" size="sm" variant={managerSignatureType === 'Draw' ? 'default' : 'outline'} onClick={() => setManagerSignatureType('Draw')} className="h-8 rounded-lg text-xs font-bold px-4">Draw</Button>
                                    </div>
                                </div>
                                {managerSignatureType === 'Type' ? (
                                    <Input
                                        placeholder="Type full name of manager"
                                        value={managerTypedSignature}
                                        onChange={(e) => setManagerTypedSignature(e.target.value)}
                                        className="rounded-xl h-12 px-4 border-gray-200 font-[signature] text-xl tracking-wider text-gray-800 bg-white"
                                    />
                                ) : (
                                    <div className="relative border border-gray-200 rounded-xl overflow-hidden bg-white touch-none">
                                        <canvas
                                            ref={managerCanvasRef}
                                            width={800} height={200} className="w-full h-[160px] cursor-crosshair"
                                            onMouseDown={(e) => { setIsManagerDrawing(true); drawSignature(e, managerCanvasRef.current, true); }}
                                            onMouseMove={(e) => { if (isManagerDrawing) drawSignature(e, managerCanvasRef.current, false); }}
                                            onMouseUp={() => setIsManagerDrawing(false)}
                                            onMouseLeave={() => setIsManagerDrawing(false)}
                                            onTouchStart={(e) => { setIsManagerDrawing(true); drawSignature(e, managerCanvasRef.current, true); }}
                                            onTouchMove={(e) => { if (isManagerDrawing) drawSignature(e, managerCanvasRef.current, false); }}
                                            onTouchEnd={() => setIsManagerDrawing(false)}
                                        />
                                        <Button type="button" variant="ghost" size="sm" onClick={() => clearCanvas(managerCanvasRef.current)} className="absolute top-2 right-2 h-8 rounded-lg text-xs font-bold text-gray-500 hover:text-red-500 bg-white shadow-sm border border-gray-100">Clear</Button>
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Current Assignee Signature */}
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Current Assignee (Optional)</label>
                                        <div className="flex gap-2">
                                            <Button type="button" size="sm" variant={currentAssigneeSignatureType === 'Type' ? 'default' : 'outline'} onClick={() => setCurrentAssigneeSignatureType('Type')} className="h-8 rounded-lg text-[10px] font-bold px-3">Type</Button>
                                            <Button type="button" size="sm" variant={currentAssigneeSignatureType === 'Draw' ? 'default' : 'outline'} onClick={() => setCurrentAssigneeSignatureType('Draw')} className="h-8 rounded-lg text-[10px] font-bold px-3">Draw</Button>
                                        </div>
                                    </div>
                                    {currentAssigneeSignatureType === 'Type' ? (
                                        <Input
                                            placeholder="Type name"
                                            value={currentAssigneeTypedSignature}
                                            onChange={(e) => setCurrentAssigneeTypedSignature(e.target.value)}
                                            className="rounded-xl h-12 px-4 border-gray-200 font-[signature] text-xl tracking-wider text-gray-800"
                                        />
                                    ) : (
                                        <div className="relative border border-gray-200 rounded-xl overflow-hidden bg-gray-50 touch-none">
                                            <canvas
                                                ref={currentAssigneeCanvasRef}
                                                width={400} height={160} className="w-full h-[120px] cursor-crosshair"
                                                onMouseDown={(e) => { setIsCurrentAssigneeDrawing(true); drawSignature(e, currentAssigneeCanvasRef.current, true); }}
                                                onMouseMove={(e) => { if (isCurrentAssigneeDrawing) drawSignature(e, currentAssigneeCanvasRef.current, false); }}
                                                onMouseUp={() => setIsCurrentAssigneeDrawing(false)}
                                                onMouseLeave={() => setIsCurrentAssigneeDrawing(false)}
                                                onTouchStart={(e) => { setIsCurrentAssigneeDrawing(true); drawSignature(e, currentAssigneeCanvasRef.current, true); }}
                                                onTouchMove={(e) => { if (isCurrentAssigneeDrawing) drawSignature(e, currentAssigneeCanvasRef.current, false); }}
                                                onTouchEnd={() => setIsCurrentAssigneeDrawing(false)}
                                            />
                                            <Button type="button" variant="ghost" size="sm" onClick={() => clearCanvas(currentAssigneeCanvasRef.current)} className="absolute top-2 right-2 h-6 px-2 rounded font-bold text-[10px] bg-white shadow-sm border border-gray-100">Clear</Button>
                                        </div>
                                    )}
                                </div>

                                {/* New Assignee Signature */}
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">New Assignee (Optional)</label>
                                        <div className="flex gap-2">
                                            <Button type="button" size="sm" variant={newAssigneeSignatureType === 'Type' ? 'default' : 'outline'} onClick={() => setNewAssigneeSignatureType('Type')} className="h-8 rounded-lg text-[10px] font-bold px-3">Type</Button>
                                            <Button type="button" size="sm" variant={newAssigneeSignatureType === 'Draw' ? 'default' : 'outline'} onClick={() => setNewAssigneeSignatureType('Draw')} className="h-8 rounded-lg text-[10px] font-bold px-3">Draw</Button>
                                        </div>
                                    </div>
                                    {newAssigneeSignatureType === 'Type' ? (
                                        <Input
                                            placeholder="Type name"
                                            value={newAssigneeTypedSignature}
                                            onChange={(e) => setNewAssigneeTypedSignature(e.target.value)}
                                            className="rounded-xl h-12 px-4 border-gray-200 font-[signature] text-xl tracking-wider text-gray-800"
                                        />
                                    ) : (
                                        <div className="relative border border-gray-200 rounded-xl overflow-hidden bg-gray-50 touch-none">
                                            <canvas
                                                ref={newAssigneeCanvasRef}
                                                width={400} height={160} className="w-full h-[120px] cursor-crosshair"
                                                onMouseDown={(e) => { setIsNewAssigneeDrawing(true); drawSignature(e, newAssigneeCanvasRef.current, true); }}
                                                onMouseMove={(e) => { if (isNewAssigneeDrawing) drawSignature(e, newAssigneeCanvasRef.current, false); }}
                                                onMouseUp={() => setIsNewAssigneeDrawing(false)}
                                                onMouseLeave={() => setIsNewAssigneeDrawing(false)}
                                                onTouchStart={(e) => { setIsNewAssigneeDrawing(true); drawSignature(e, newAssigneeCanvasRef.current, true); }}
                                                onTouchMove={(e) => { if (isNewAssigneeDrawing) drawSignature(e, newAssigneeCanvasRef.current, false); }}
                                                onTouchEnd={() => setIsNewAssigneeDrawing(false)}
                                            />
                                            <Button type="button" variant="ghost" size="sm" onClick={() => clearCanvas(newAssigneeCanvasRef.current)} className="absolute top-2 right-2 h-6 px-2 rounded font-bold text-[10px] bg-white shadow-sm border border-gray-100">Clear</Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </section>

                        <div className="flex justify-end gap-4 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate(-1)}
                                className="h-14 rounded-2xl px-8 font-bold text-gray-700 bg-white hover:bg-gray-50 border-gray-200"
                                disabled={submitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="h-14 rounded-2xl px-8 font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-xl shadow-blue-600/20"
                                disabled={submitting}
                            >
                                {submitting ? 'Processing...' : (
                                    <span className="flex items-center gap-2">
                                        <Save size={20} />
                                        Complete Reassignment
                                    </span>
                                )}
                            </Button>
                        </div>
                    </form>
                </div>

                {/* Info Sidebar */}
                <div className="hidden lg:block space-y-6">
                    <div className="bg-gray-50/50 p-6 rounded-3xl border border-gray-100">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-500">
                                <Signature size={20} />
                            </div>
                            <h3 className="font-bold text-gray-900 text-sm">Chain of Custody</h3>
                        </div>
                        <div className="space-y-4 text-sm font-medium text-gray-600">
                            <p>
                                By reassigning this asset directly, you are streamlining the return and assignment steps into one seamless handover process.
                            </p>
                            <ul className="space-y-3 pt-2 border-t border-gray-200/60">
                                <li className="flex items-start gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                                    <span>Manager's approval via signature is mandatory.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                                    <span>Emails will be sent simultaneously to confirm transfer to all parties involved.</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReassignAsset;
